import { FC, ReactNode, useCallback, useMemo, useRef } from "react";
import {
  ChatInput,
  ChatInputHandle,
  ChatInputParams,
} from "@web-src/modules/chats/components/ChatInput";
import {
  EmailConversationModel,
  parseEmailConvId,
  useRestApiProvider,
} from "@jugl-web/rest-api";
import { useTranslations } from "@jugl-web/utils";
import { AttachmentType } from "@web-src/modules/chats/types";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import { useClientSupportPageContext } from "../../../../providers/ClientSupportPageProvider/useClientSupportPageContext";
import { useEmailAccount } from "../../../../providers/EmailAccountProvider/useEmailAccount";
import { useManageAccountsDrawer } from "../../../ManageAccountsDrawer/providers/ManageAccountsDrawerContext";
import { DisconnectedChatInput } from "../DisconnectedChatInput";
import { ReactComponent as ReplyIcon } from "./assets/reply.svg";

export const EmailChatInput: FC<{
  conversation: EmailConversationModel;
  onMessageSent?: () => void;
}> = ({ conversation, onMessageSent }) => {
  const { t } = useTranslations();
  const { setIsSendingMessage, incomingEmailMessages$ } =
    useClientSupportPageContext();
  const { entityId } = useEntitySelectedProvider();
  const { integrationsApi } = useRestApiProvider();
  const { isConnected } = useEmailAccount();
  const { openManageAccountDrawer } = useManageAccountsDrawer();
  const [sendEmailMessage, { isLoading: isSendingEmailMessage }] =
    integrationsApi.useSendEmailMessageMutation();
  const chatInput$ = useRef<ChatInputHandle>(null);

  const { email: recipientEmail } = useMemo(
    () => parseEmailConvId(conversation.conv_id),
    [conversation.conv_id]
  );

  const handleSendMessage: ChatInputParams["onSubmit"] = useCallback(
    async ({ body, bodyHtml, attachments }) => {
      if (!body.trim() && !attachments?.length) return;

      try {
        setIsSendingMessage(true);

        const sentMessage = await sendEmailMessage({
          entityId,
          data: {
            message_id: conversation.payload.messageId ?? "",
            name: conversation.conv_id,
            text: (bodyHtml ?? body).trim(),
            files: attachments?.map((attachment) => attachment.file),
          },
        }).unwrap();

        incomingEmailMessages$.next(sentMessage);

        setTimeout(() => {
          // clear() resets the body AND attachments; setValue("") leaves
          // attachments stuck in the composer
          chatInput$.current?.clear();
        }, 100);
        chatInput$.current?.focus();
        onMessageSent?.();
      } catch (error) {
        // NOTE: pass
      } finally {
        setIsSendingMessage(false);
      }
    },
    [
      conversation,
      entityId,
      sendEmailMessage,
      onMessageSent,
      setIsSendingMessage,
      incomingEmailMessages$,
    ]
  );

  if (!isConnected) {
    return (
      <DisconnectedChatInput
        title={t({
          id: "client-support-page.account-is-disconnected",
          defaultMessage: "Account is Disconnected",
        })}
        description={t(
          {
            id: "client-support-page.please-reconnect-to-continue-using-gmail-inbox",
            defaultMessage:
              "Please <highlightedText>reconnect</highlightedText> to continue using Gmail Inbox",
          },
          {
            highlightedText: (text: ReactNode) => (
              <span className="font-medium text-primary">{text}</span>
            ),
          }
        )}
        buttonProps={{
          text: t({
            id: "client-support-page.open-settings",
            defaultMessage: "Open Settings",
          }),
          onClick: () => openManageAccountDrawer({ name: "emailAccount" }),
        }}
      />
    );
  }

  return (
    <div className="border-0 border-t border-solid border-dark-100">
      <div className="flex items-center gap-2 border-0 border-b border-solid border-dark-100 px-8 py-2.5">
        <ReplyIcon className="shrink-0" />
        <span className="truncate font-secondary text-xs text-[#828282]">
          {t(
            {
              id: "client-support-page.reply-to-email",
              defaultMessage: "To: {email}",
            },
            { email: recipientEmail }
          )}
        </span>
      </div>
      <div className="px-8 py-6">
        <ChatInput
          variant="inbox"
          id={conversation.conv_id}
          ref={chatInput$}
          onSubmit={handleSendMessage}
          isLoading={isSendingEmailMessage}
          placeholder={t({
            id: "client-support-page.enter-reply",
            defaultMessage: "Enter reply",
          })}
          toolbarActions={{
            emoji: {
              enabled: true,
            },
            attachments: {
              enabled: true,
              isMultiple: true,
              isCaptionHidden: true,
              accept: {
                [AttachmentType.image]: ".jpg,.jpeg,.png,.gif,.webp,.bmp,.svg",
                [AttachmentType.video]: ".mp4,.mov,.avi,.webm,.mkv,.3gp",
                [AttachmentType.audio]: ".mp3,.m4a,.aac,.ogg,.wav,.amr",
                [AttachmentType.file]:
                  ".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.zip,.rar,.7z",
              },
              maxSize: {
                [AttachmentType.image]: 25,
                [AttachmentType.video]: 25,
                [AttachmentType.audio]: 25,
                [AttachmentType.file]: 25,
              },
            },
            textFormatting: {
              enabled: true,
            },
          }}
        />
      </div>
    </div>
  );
};
